import React, { useMemo } from 'react'
import styled from 'styled-components'
import { useWeb3React } from '@web3-react/core'
import { Text, Flex, Grid, Button, AutoRenewIcon } from 'uikit'
import { useTranslation } from 'contexts/Localization'
import Page from 'components/Layout/Page'
import { useMultiPool } from 'state/hooks'
import { formatValue, getPngImageUrlFromToken } from 'utils'
import UnlockButton from 'components/UnlockButton'
import StakeForm from './components/StakeForm'
import WithdrawForm from './components/WithdrawForm'
import useApproveMultiPool from './hooks/useApproveMultiPool'
import useGetRewardMultiPool from './hooks/useGetRewardMultiPool'

const Hero = styled.div`
  background: #9012fe; /* fallback */
  background-image: url('IMAGE_URL'); /* fallback */
  background-image: linear-gradient(94.61deg, ${({ theme }) => theme.colors.background2} 15.09%, #9012fe 90.14%);
  ${({ theme }) => theme.mediaQueries.md} {
    background-image: url('/images/pools/3d-model.png'),
      linear-gradient(94.61deg, ${({ theme }) => theme.colors.background2} 15.09%, #9012fe 90.14%); /* W3C */
  }

  background-repeat: no-repeat;
  background-position: top right 15%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: left;
  color: white;
  height: 245px;
  margin: auto;
  margin-bottom: 32px;
  padding-left: 40px;
  // -webkit-box-shadow: 0px 4px 4px 0px ${({ theme }) => theme.colors.background2};
  // box-shadow: 0px 4px 4px 0px ${({ theme }) => theme.colors.background2};

  ${({ theme }) => theme.mediaQueries.md} {
    border-radius: 24px;
  }
`

const Title = styled.div`
  font-size: 50px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #ffffff;
`

const Subtitle = styled.div`
  font-weight: 300;
  font-size: 18px;
`

const MultiPoolCard = styled.div`
  border-radius: 15px;
  background-color: #361c7f;
  max-width: 900px;
  margin: 0 20px;

  ${({ theme }) => theme.mediaQueries.sm} {
    margin: auto;
  }
`

const HeadingWrapper = styled.div`
  padding: 16px 24px 24px 24px;
  border-radius: 15px 15px 0px 0px;
  background: linear-gradient(90deg, #c45bec, #9112ff);
`

const SubheadingWrapper = styled(Flex)`
  padding: 16px 24px 16px 24px;
  border-radius: 0px 0px 10px 10px;
  background: #6700c1;
`

const BodyWrapper = styled(Grid)`
  padding: 12px 24px 24px 24px;
  grid-gap: 44px;
  flex-direction: column;

  ${({ theme }) => theme.mediaQueries['2xl']} {
    flex-direction: row;
  }
`

const EarningTokens = styled.div`
  width: 100%;
`

const Divider = styled.div`
  margin-top: 12px;
  border: 0.2px solid #ffffff;
`

const StyledButton = styled(Button)`
  height: 64.5px;
  margin-top: 20px;
  width: 100%;
`

const TokenImage = styled.img`
  width: 35px;
  height: 35px;
  border-radius: 20px;
  background: white;
  margin-left: -10px;
`

const MedalIcon = styled.img`
  margin-left: 12px;
  margin-bottom: 4px;
`

const MultiPool: React.FC = () => {
  const { t } = useTranslation()
  const { account } = useWeb3React()
  const { data: multiPool } = useMultiPool()
  const { isApproving, handleApproveMultiPool } = useApproveMultiPool()
  const { isGettingReward, handleGetRewardMultiPool } = useGetRewardMultiPool()

  const totalApr = useMemo(
    () => multiPool.earningTokensApr?.reduce((accum, curr) => accum + curr) || 0,
    [multiPool.earningTokensApr],
  )
  const totalEarned = useMemo(
    () => multiPool.userData?.earns.reduce((accum, earned) => accum + earned.usdVaule, 0) || 0,
    [multiPool.userData],
  )
  const isApproved = account && multiPool.userData?.allowance?.isGreaterThan(0)

  const renderEarningTokens = (
    <EarningTokens>
      <Text fontWeight="bold" fontSize="15px" color="textSecondary">
        {t('APR available')}
      </Text>
      {multiPool.earningTokens.map((earningToken, index) => (
        <Flex key={earningToken.symbol} mt="4px" justifyContent="space-between">
          <Text fontSize="15px" fontWeight={600}>
            {earningToken.symbol}
          </Text>
          <Text fontSize="15px" fontWeight={600}>
            {`${multiPool.earningTokensApr ? multiPool.earningTokensApr[index].toFixed(2) : 0}%`}
          </Text>
        </Flex>
      ))}
      <Divider />
    </EarningTokens>
  )

  const renderRewardEarned = (
    <EarningTokens>
      <Text fontWeight="bold" fontSize="15px" color="textSecondary">
        {t('Reward earned')}
      </Text>
      {multiPool.earningTokens.map((earningToken, index) => (
        <Flex key={earningToken.symbol} mt="4px">
          <Flex justifyContent="space-between" width="calc(100% - 72px)" alignItems="center">
            <Text fontSize="15px" fontWeight={600}>
              {earningToken.symbol}
            </Text>
            <Text fontSize="12px" fontWeight={300} color="textSecondary" mt="2px">
              {`${multiPool.userData?.earns ? multiPool.userData?.earns[index].usdVaule.toFixed(2) : 0} USD`}
            </Text>
          </Flex>
          <Text fontSize="15px" fontWeight={600} width="72px" textAlign="right">
            {multiPool.userData?.earns ? formatValue(multiPool.userData?.earns[index].tokenValue) : 0}
          </Text>
        </Flex>
      ))}
      <Divider />
    </EarningTokens>
  )

  return (
    <Page color="white">
      <Hero>
        <Title>{t('Multi Pool')}</Title>
        <Subtitle>{t('Put your DMD to Work')}</Subtitle>
      </Hero>
      <MultiPoolCard>
        <HeadingWrapper>
          <Text fontSize="25px" fontWeight={600}>
            {t('Stake DMD, Earn Tokens')}
          </Text>
          <Flex mt="4px" justifyContent="end" flexDirection="row-reverse">
            {multiPool.earningTokens.map((earningToken) => (
              <TokenImage
                key={earningToken.symbol}
                alt={earningToken.symbol}
                src={getPngImageUrlFromToken(earningToken)}
              />
            ))}
          </Flex>
        </HeadingWrapper>
        <SubheadingWrapper justifyContent="space-between" alignItems="center">
          <Text fontSize="18px" fontWeight={600}>
            {t('Total APR')}
          </Text>
          <Text fontSize="25px" fontWeight={600}>{`${totalApr.toFixed(2)}%`}</Text>
        </SubheadingWrapper>
        <BodyWrapper gridTemplateColumns={[null, null, null, null, null, null, '1fr 1fr']} gridColumnGap="44px">
          <div>
            {renderEarningTokens}
            {account ? (
              <>
                {isApproved ? (
                  <>
                    <StakeForm />
                    <WithdrawForm stakedBalance={multiPool.userData?.stakedBalance} />
                  </>
                ) : (
                  <StyledButton
                    disabled={isApproving}
                    onClick={() => handleApproveMultiPool()}
                    isLoading={isApproving}
                    endIcon={isApproving ? <AutoRenewIcon spin color="currentColor" /> : null}
                  >
                    {isApproving ? t('Approving') : t('Approve')}
                  </StyledButton>
                )}
              </>
            ) : (
              <UnlockButton width="100%" mt={20} height="64.5px" />
            )}
          </div>
          <div>
            {renderRewardEarned}
            {account && isApproved && (
              <>
                <Flex
                  justifyContent="space-between"
                  mt="20px"
                  alignItems="center"
                  borderBottom="3px solid #8E12FC"
                  height="64.5px"
                >
                  <Text fontSize="20px" fontWeight={600}>
                    {t('Total')}
                  </Text>
                  <Text fontSize="20px" fontWeight={600}>{`${totalEarned.toFixed(2) || 0} USD`}</Text>
                </Flex>
                <StyledButton
                  disabled={isGettingReward}
                  onClick={() => handleGetRewardMultiPool()}
                  isLoading={isGettingReward}
                  endIcon={isGettingReward ? <AutoRenewIcon spin color="currentColor" /> : null}
                >
                  {isGettingReward ? t('Getting reward') : t('Get reward')}
                  <MedalIcon src="/images/multipool/medal.svg" alt="medal" />
                </StyledButton>
              </>
            )}
          </div>
        </BodyWrapper>
      </MultiPoolCard>
    </Page>
  )
}

export default MultiPool
